import { useEffect, useMemo, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";

import { DetailLinkWithEntityPeek } from "@/components/hub/DetailLinkWithEntityPeek";
import { PurchaseInvoiceDetailLink, WorkOrderDetailLink } from "@/components/hub/HubEntityDetailLink";
import { useDeferredMrpSkuHoverHints } from "@/components/mrp/MrpSkuHoverPeek";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { DateInput, Input } from "@/components/ui/Input";
import { CardTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { ApiError } from "@/lib/api";
import { buildMrpSkuDetailPath, type MrpSkuHoverHintFields } from "@/lib/mrp";
import { cn, EM_DASH } from "@/lib/utils";

import { formatManufacturerInvoiceDate, manufacturerInvoiceDateInputValue } from "./suntronicInvoiceDate";
import { formatMoney, formatQuantity, manufacturerInvoiceTotalsMatch } from "./suntronicInvoicesFormat";
import { SuntronicInvoiceTableEmpty, SuntronicInvoiceTableShell } from "./suntronicInvoiceTableShell";
import { SuntronicInvoiceCreateGipiModal } from "./SuntronicInvoiceCreateGipiModal";
import { SuntronicInvoiceShipmentsTable } from "./suntronicInvoicesShipmentsTable";
import {
  createSuntronicInvoiceMatch,
  deleteSuntronicInvoiceMatch,
  fetchSuntronicInvoiceFinishedGoods,
  fetchSuntronicInvoiceLinkedGipis,
  fetchSuntronicInvoiceMatches,
  fetchSuntronicInvoiceShipments,
  fetchSuntronicInvoiceWorkOrders,
  patchSuntronicInvoiceDate,
  updateSuntronicInvoiceMatchQuantity,
  type SuntronicInvoiceFinishedGoodRow,
  type SuntronicInvoiceMatchRow,
  type SuntronicInvoiceWorkOrderRow,
} from "./suntronicInvoicesApi";
import { filterSuntronicInvoiceShipmentRows } from "./suntronicInvoiceShipmentsSearch";

const SUNTRONIC_QUERY_ROOT = ["accounting", "suntronic-invoices"] as const;
const READ_ONLY_HINT = "Your role has read-only access to the Accounting hub module.";
const ENTITY_LINK_CLASS =
  "font-medium text-sky-700 no-underline hover:text-sky-900 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-1";

function isolateRowSelect(event: MouseEvent): void {
  event.stopPropagation();
}

function parsePositiveInt(value: number | string | null | undefined): number | null {
  if (value == null || value === "") return null;
  const parsed = typeof value === "number" ? value : Number.parseInt(String(value), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

type SourceTab = "shipments" | "work-orders";

function toNumber(value: number | string | null | undefined): number {
  if (value == null) return 0;
  const parsed = typeof value === "number" ? value : Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function describeMutationError(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}

export function SuntronicInvoiceWorkMatchingColumn({
  invoiceId,
  invoiceTotal,
  invoiceNumber,
  manufacturer,
  invoiceDate,
  canWrite,
  reconciled,
  onError,
}: {
  invoiceId: number;
  invoiceTotal: string;
  invoiceNumber: string;
  manufacturer: string;
  invoiceDate: string | null;
  canWrite: boolean;
  reconciled: boolean;
  onError: (message: string) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const [sourceTab, setSourceTab] = useState<SourceTab>("shipments");
  const [selectedShipmentId, setSelectedShipmentId] = useState<number | null>(null);
  const [selectedBatchItemId, setSelectedBatchItemId] = useState<number | null>(null);
  const [selectedWorkOrderBuildOrderId, setSelectedWorkOrderBuildOrderId] = useState<number | null>(null);
  const [selectedFinishedGoodId, setSelectedFinishedGoodId] = useState<number | null>(null);
  const [hideGso, setHideGso] = useState(true);
  const [shipmentSearch, setShipmentSearch] = useState("");
  const [workOrderSearch, setWorkOrderSearch] = useState("");
  const [createGipiOpen, setCreateGipiOpen] = useState(false);

  const matchingLocked = reconciled || !canWrite;

  const matchesQuery = useQuery({
    queryKey: [...SUNTRONIC_QUERY_ROOT, "invoice-matches", invoiceId],
    queryFn: () => fetchSuntronicInvoiceMatches(invoiceId),
  });

  const linkedGipisQuery = useQuery({
    queryKey: [...SUNTRONIC_QUERY_ROOT, "linked-gipis", invoiceId],
    queryFn: () => fetchSuntronicInvoiceLinkedGipis(invoiceId),
  });

  const shipmentsQuery = useQuery({
    queryKey: [...SUNTRONIC_QUERY_ROOT, "shipments"],
    queryFn: fetchSuntronicInvoiceShipments,
    enabled: sourceTab === "shipments",
  });

  const workOrdersQuery = useQuery({
    queryKey: [...SUNTRONIC_QUERY_ROOT, "work-orders", hideGso],
    queryFn: () => fetchSuntronicInvoiceWorkOrders(hideGso),
    enabled: sourceTab === "work-orders",
  });

  const selectedShipment = useMemo(
    () =>
      shipmentsQuery.data?.find(
        (row) => row.shipment_id === selectedShipmentId && row.batch_item_id === selectedBatchItemId,
      ) ?? null,
    [shipmentsQuery.data, selectedShipmentId, selectedBatchItemId],
  );

  const buildOrderFilterId =
    sourceTab === "shipments" ? selectedShipment?.build_order_id ?? null : selectedWorkOrderBuildOrderId;

  const finishedGoodsQuery = useQuery({
    queryKey: [...SUNTRONIC_QUERY_ROOT, "finished-goods", buildOrderFilterId],
    queryFn: () => fetchSuntronicInvoiceFinishedGoods(buildOrderFilterId!),
    enabled: buildOrderFilterId != null,
  });

  const filteredShipments = useMemo(
    () => filterSuntronicInvoiceShipmentRows(shipmentsQuery.data ?? [], shipmentSearch),
    [shipmentsQuery.data, shipmentSearch],
  );

  const filteredWorkOrders = useMemo(() => {
    const rows = workOrdersQuery.data ?? [];
    const needle = workOrderSearch.trim().toLowerCase();
    if (needle === "") return rows;
    return rows.filter((row) =>
      [row.WorkOrderNo, row.sb_number, row.WorkOrderFinishedGoodSKU, row.WorkOrderReferenceNo, row.sb_mfr_reference_number]
        .map((value) => String(value ?? "").toLowerCase())
        .some((value) => value.includes(needle)),
    );
  }, [workOrdersQuery.data, workOrderSearch]);

  const finishedGoodsToMatch = finishedGoodsQuery.data ?? [];
  const matches = matchesQuery.data ?? [];
  const matchSkus = useMemo(
    () => matches.flatMap((row) => [row.sku, row.tla_sku].filter((sku): sku is string => sku != null && sku.trim() !== "")),
    [matches],
  );
  const { skuHoverHints, hintsLoading, enableHintsFetch } = useDeferredMrpSkuHoverHints(
    matchSkus,
    "suntronic-invoice-matches",
  );
  const linkedGipis = linkedGipisQuery.data ?? [];
  const linkedGipiTotal = linkedGipis.reduce((sum, row) => sum + toNumber(row.invoice_total), 0);
  const matchedServiceCostTotal = matches.reduce((sum, row) => sum + toNumber(row.service_cost), 0);
  const totalsMatch = manufacturerInvoiceTotalsMatch(matchedServiceCostTotal, invoiceTotal);

  const invalidateMatching = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [...SUNTRONIC_QUERY_ROOT, "shipments"] }),
      queryClient.invalidateQueries({ queryKey: [...SUNTRONIC_QUERY_ROOT, "finished-goods"] }),
      queryClient.invalidateQueries({ queryKey: [...SUNTRONIC_QUERY_ROOT, "invoice-matches"] }),
      queryClient.invalidateQueries({ queryKey: [...SUNTRONIC_QUERY_ROOT, "work-orders"] }),
      queryClient.invalidateQueries({ queryKey: [...SUNTRONIC_QUERY_ROOT, "invoice-options"] }),
      queryClient.invalidateQueries({ queryKey: [...SUNTRONIC_QUERY_ROOT, "linked-gipis"] }),
    ]);
  };

  const matchMutation = useMutation({
    mutationFn: async () => {
      if (selectedFinishedGoodId == null) {
        throw new Error("Select a finished good to match.");
      }
      const finishedGood = finishedGoodsToMatch.find((row) => row.id === selectedFinishedGoodId);
      if (finishedGood == null) {
        throw new Error("Selected finished good is no longer available.");
      }
      if (sourceTab === "shipments") {
        if (selectedShipment == null) {
          throw new Error("Select a shipment row first.");
        }
        const remainingQuantity = toNumber(selectedShipment.remaining_quantity);
        if (remainingQuantity <= 0) {
          throw new Error("This shipment line has no remaining quantity to match.");
        }
        await createSuntronicInvoiceMatch({
          mfr_invoice_id: invoiceId,
          finished_good_id: selectedFinishedGoodId,
          quantity: remainingQuantity,
          batch_id: selectedShipment.batch_id,
          shipment_id: selectedShipment.shipment_id,
          batch_item_id: selectedShipment.batch_item_id,
        });
        return;
      }
      await createSuntronicInvoiceMatch({
        mfr_invoice_id: invoiceId,
        finished_good_id: selectedFinishedGoodId,
        quantity: toNumber(finishedGood.quantity),
      });
    },
    onSuccess: async () => {
      await invalidateMatching();
    },
    onError: (error) => {
      onError(describeMutationError(error, "Could not create invoice match."));
    },
  });

  const deleteMatchMutation = useMutation({
    mutationFn: deleteSuntronicInvoiceMatch,
    onSuccess: async () => {
      await invalidateMatching();
    },
    onError: (error) => {
      onError(describeMutationError(error, "Could not delete match."));
    },
  });

  const quantityMutation = useMutation({
    mutationFn: ({ matchId, quantity }: { matchId: number; quantity: number }) =>
      updateSuntronicInvoiceMatchQuantity(matchId, quantity),
    onSuccess: async () => {
      await invalidateMatching();
    },
    onError: (error) => {
      onError(describeMutationError(error, "Could not update match quantity."));
    },
  });

  const dateMutation = useMutation({
    mutationFn: (date: string) => patchSuntronicInvoiceDate(invoiceId, date),
    onSuccess: async () => {
      await invalidateMatching();
    },
    onError: (error) => {
      onError(describeMutationError(error, "Could not update the manufacturer invoice date."));
    },
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
      <Card className="shrink-0">
        <CardHeader className="py-3">
          <CardTitleWithHelp hoverHint="Manufacturer invoice header and finished goods already matched to it.">
            Invoice matches
          </CardTitleWithHelp>
        </CardHeader>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 overflow-hidden border-b border-slate-100 px-5 py-2 sm:grid-cols-5">
          <div className="min-w-0">
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Manufacturer</p>
            <p className="truncate text-sm text-slate-900">{manufacturer}</p>
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Date</p>
            {canWrite && !reconciled ? (
              <DateInput
                className="h-8 px-2"
                value={manufacturerInvoiceDateInputValue(invoiceDate)}
                disabled={dateMutation.isPending}
                onChange={(event) => {
                  const nextDate = event.target.value;
                  if (nextDate === "" || nextDate === manufacturerInvoiceDateInputValue(invoiceDate)) {
                    return;
                  }
                  dateMutation.mutate(nextDate);
                }}
              />
            ) : (
              <p className="text-sm text-slate-900">{formatManufacturerInvoiceDate(invoiceDate)}</p>
            )}
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Total</p>
            <p className="text-sm tabular-nums text-slate-900">{formatMoney(invoiceTotal)}</p>
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Status</p>
            <p className="pt-0.5">
              <Badge tone={reconciled ? "success" : "warning"}>{reconciled ? "Reconciled" : "Open"}</Badge>
            </p>
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">GIPI</p>
            {linkedGipisQuery.isLoading ? (
              <p className="text-sm text-slate-500">Loading…</p>
            ) : linkedGipisQuery.isError ? (
              <p className="text-sm text-rose-700">Could not load GIPI.</p>
            ) : linkedGipis.length === 0 ? (
              <p className="text-sm text-slate-900">{EM_DASH}</p>
            ) : (
              <div className="space-y-1">
                {linkedGipis.map((row) => (
                  <div key={row.purchase_invoice_id} className="min-w-0">
                    <PurchaseInvoiceDetailLink
                      purchaseInvoiceId={row.purchase_invoice_id}
                      mvNumber={row.mv_number}
                      vendorInvoiceNumber={row.vendor_invoice_number}
                      className={ENTITY_LINK_CLASS}
                    />
                    <p className="text-xs tabular-nums text-slate-600">{formatMoney(row.invoice_total)}</p>
                    {row.expected_due_date != null ? (
                      <p className="text-xs text-slate-500">
                        Expected due {formatManufacturerInvoiceDate(row.expected_due_date)}
                      </p>
                    ) : null}
                  </div>
                ))}
                {linkedGipis.length > 1 ? (
                  <p className="text-xs font-medium tabular-nums text-slate-800">
                    Linked total {formatMoney(linkedGipiTotal)}
                  </p>
                ) : null}
              </div>
            )}
          </div>
        </div>
        <CardBody className="p-0">
          {matchesQuery.isLoading ? (
            <div className="flex items-center gap-2 px-5 py-4 text-sm text-slate-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading matches…
            </div>
          ) : matchesQuery.isError ? (
            <p className="px-5 py-4 text-sm text-rose-700">Could not load invoice matches.</p>
          ) : matches.length === 0 ? (
            <SuntronicInvoiceTableEmpty>No matches on this invoice yet.</SuntronicInvoiceTableEmpty>
          ) : (
            <SuntronicInvoiceTableShell>
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2">WOFG</th>
                  <th className="px-4 py-2">SKU</th>
                  <th className="px-4 py-2">TLA SKU</th>
                  <th className="px-4 py-2 text-right">Qty</th>
                  <th className="px-4 py-2 text-right">Service cost</th>
                  <th className="px-4 py-2">TLA orders</th>
                  {matchingLocked ? null : <th className="px-4 py-2">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {matches.map((row) => (
                  <MatchRow
                    key={row.id}
                    row={row}
                    showActions={!matchingLocked}
                    canRemove={!matchingLocked && !deleteMatchMutation.isPending}
                    onRemove={() => deleteMatchMutation.mutate(row.id)}
                    canEditQuantity={!matchingLocked && !quantityMutation.isPending}
                    onQuantityCommit={(quantity) => quantityMutation.mutate({ matchId: row.id, quantity })}
                    skuHint={row.sku != null ? skuHoverHints?.[row.sku.trim()] : undefined}
                    tlaSkuHint={row.tla_sku != null ? skuHoverHints?.[row.tla_sku.trim()] : undefined}
                    skuHintsLoading={hintsLoading}
                    onSkuHoverIntent={enableHintsFetch}
                  />
                ))}
              </tbody>
            </SuntronicInvoiceTableShell>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-5 py-3">
            <div className="space-y-0.5">
              <p className="text-sm font-semibold text-slate-900">
                Matched: {formatMoney(matchedServiceCostTotal)} / {formatMoney(invoiceTotal)}
              </p>
              {linkedGipis.length > 0 ? (
                <p className="text-sm tabular-nums text-slate-700">
                  Linked GIPI: {formatMoney(linkedGipiTotal)} / {formatMoney(invoiceTotal)}
                </p>
              ) : null}
            </div>
            {totalsMatch && !reconciled ? (
              <ReadOnlyHoverLayer active={!canWrite} hoverHint={READ_ONLY_HINT} layout="inline">
                <Button type="button" disabled={!canWrite} onClick={() => setCreateGipiOpen(true)}>
                  Create GIPI
                </Button>
              </ReadOnlyHoverLayer>
            ) : null}
          </div>
        </CardBody>
      </Card>

      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <CardHeader className="shrink-0">
          <CardTitleWithHelp hoverHint="Pick a shipment or work order, then a finished good, and match it to this invoice.">
            Finished goods to match
          </CardTitleWithHelp>
        </CardHeader>
        <CardBody className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant={sourceTab === "shipments" ? "primary" : "secondary"}
              onClick={() => {
                setSourceTab("shipments");
                setSelectedWorkOrderBuildOrderId(null);
                setSelectedFinishedGoodId(null);
                setSelectedBatchItemId(null);
              }}
            >
              Shipments
            </Button>
            <Button
              type="button"
              size="sm"
              variant={sourceTab === "work-orders" ? "primary" : "secondary"}
              onClick={() => {
                setSourceTab("work-orders");
                setSelectedShipmentId(null);
                setSelectedBatchItemId(null);
                setSelectedFinishedGoodId(null);
              }}
            >
              Work orders
            </Button>
          </div>
          {sourceTab === "shipments" ? (
            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
              <Input
                value={shipmentSearch}
                onChange={(event) => setShipmentSearch(event.target.value)}
                placeholder="Filter by shipment, batch:1767, @batch 1767, SKU, or location..."
                type="search"
                className="shrink-0"
              />
              {shipmentsQuery.isLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading shipments…
                </div>
              ) : (
                <SuntronicInvoiceShipmentsTable
                  rows={filteredShipments}
                  selectedShipmentId={selectedShipmentId}
                  selectedBatchItemId={selectedBatchItemId}
                  fillAvailableHeight
                  onSelect={(row) => {
                    setSelectedShipmentId(row.shipment_id);
                    setSelectedBatchItemId(row.batch_item_id);
                    setSelectedFinishedGoodId(null);
                  }}
                />
              )}
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={hideGso}
                  onChange={(event) => setHideGso(event.target.checked)}
                />
                Hide GSO orders
              </label>
              <Input
                value={workOrderSearch}
                onChange={(event) => setWorkOrderSearch(event.target.value)}
                placeholder="Filter by work order, SKU, or reference..."
                type="search"
              />
              {workOrdersQuery.isLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading work orders…
                </div>
              ) : workOrdersQuery.isError ? (
                <p className="text-sm text-rose-700">Could not load work orders.</p>
              ) : filteredWorkOrders.length === 0 ? (
                <p className="text-sm text-slate-600">No work orders match these filters.</p>
              ) : (
                <SuntronicInvoiceTableShell className="min-h-0 flex-1 overflow-y-auto" allowVerticalScroll>
                  <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-2">MV number</th>
                      <th className="px-3 py-2">Build order</th>
                      <th className="px-3 py-2">SKU</th>
                      <th className="px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredWorkOrders.map((row) => (
                      <WorkOrderPickerRow
                        key={row.WorkOrderId}
                        row={row}
                        selected={row.sb_id === selectedWorkOrderBuildOrderId}
                        onSelect={() => {
                          setSelectedWorkOrderBuildOrderId(row.sb_id > 0 ? row.sb_id : null);
                          setSelectedFinishedGoodId(null);
                        }}
                      />
                    ))}
                  </tbody>
                </SuntronicInvoiceTableShell>
              )}
            </div>
          )}

          {buildOrderFilterId == null ? (
            <SuntronicInvoiceTableEmpty>
              Select a shipment or work order to see finished goods.
            </SuntronicInvoiceTableEmpty>
          ) : finishedGoodsQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading finished goods…
            </div>
          ) : finishedGoodsQuery.isError ? (
            <p className="px-5 py-4 text-sm text-rose-700">Could not load finished goods.</p>
          ) : finishedGoodsToMatch.length === 0 ? (
            <SuntronicInvoiceTableEmpty>No finished goods for this selection.</SuntronicInvoiceTableEmpty>
          ) : (
            <SuntronicInvoiceTableShell>
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2">SKU</th>
                  <th className="px-4 py-2 text-right">Qty</th>
                  <th className="px-4 py-2 text-right">Matched</th>
                  <th className="px-4 py-2">MV build order</th>
                  <th className="px-4 py-2">WOFG</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {finishedGoodsToMatch.map((row) => (
                  <FinishedGoodPickerRow
                    key={row.id}
                    row={row}
                    selected={selectedFinishedGoodId === row.id}
                    onSelect={() => setSelectedFinishedGoodId(row.id)}
                  />
                ))}
              </tbody>
            </SuntronicInvoiceTableShell>
          )}

          <div className="flex shrink-0 justify-end">
            <ReadOnlyHoverLayer
              active={matchingLocked}
              hoverHint={reconciled ? "Matches cannot be changed after this invoice is reconciled." : READ_ONLY_HINT}
              layout="inline"
            >
              <Button
                type="button"
                disabled={
                  matchingLocked ||
                  matchMutation.isPending ||
                  selectedFinishedGoodId == null ||
                  buildOrderFilterId == null
                }
                onClick={() => matchMutation.mutate()}
              >
                {matchMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Match
              </Button>
            </ReadOnlyHoverLayer>
          </div>
        </CardBody>
      </Card>
      {createGipiOpen ? (
        <SuntronicInvoiceCreateGipiModal
          invoiceId={invoiceId}
          invoiceNumber={invoiceNumber}
          onClose={() => setCreateGipiOpen(false)}
          onCreated={async (result) => {
            showToast({
              tone: "success",
              message: `GIPI created (Megaventory document ${result.mv_document_id}).`,
            });
            await invalidateMatching();
          }}
        />
      ) : null}
    </div>
  );
}

function MatchSkuLink({
  sku,
  skuHint,
  skuHintsLoading,
  onSkuHoverIntent,
}: {
  sku: string | null;
  skuHint: MrpSkuHoverHintFields | undefined;
  skuHintsLoading: boolean;
  onSkuHoverIntent: () => void;
}): ReactNode {
  const trimmed = sku?.trim() ?? "";
  if (trimmed === "") return EM_DASH;
  return (
    <DetailLinkWithEntityPeek
      peek={{ kind: "sku_catalog", sku: trimmed, hint: skuHint, loading: skuHintsLoading }}
      trigger={
        <Link
          to={buildMrpSkuDetailPath(trimmed, { tab: "inventory" })}
          className={ENTITY_LINK_CLASS}
          onPointerEnter={onSkuHoverIntent}
          onFocus={onSkuHoverIntent}
        >
          {trimmed}
        </Link>
      }
    />
  );
}

function WofgWorkOrderLink({
  buildOrderId,
  wofgNumber,
  isolateClick = false,
}: {
  buildOrderId: number | string | null | undefined;
  wofgNumber: string | null | undefined;
  isolateClick?: boolean;
}): ReactNode {
  const label = wofgNumber?.trim() ?? "";
  const workOrderId = parsePositiveInt(buildOrderId);
  if (label === "") return EM_DASH;
  if (workOrderId == null) return label;
  const link = (
    <WorkOrderDetailLink buildOrderId={workOrderId} className={cn(ENTITY_LINK_CLASS, "tabular-nums")}>
      {label}
    </WorkOrderDetailLink>
  );
  if (!isolateClick) return link;
  return <span onClick={isolateRowSelect}>{link}</span>;
}

function MatchRow({
  row,
  showActions,
  canRemove,
  onRemove,
  canEditQuantity,
  onQuantityCommit,
  skuHint,
  tlaSkuHint,
  skuHintsLoading,
  onSkuHoverIntent,
}: {
  row: SuntronicInvoiceMatchRow;
  showActions: boolean;
  canRemove: boolean;
  onRemove: () => void;
  canEditQuantity: boolean;
  onQuantityCommit: (quantity: number) => void;
  skuHint: MrpSkuHoverHintFields | undefined;
  tlaSkuHint: MrpSkuHoverHintFields | undefined;
  skuHintsLoading: boolean;
  onSkuHoverIntent: () => void;
}): JSX.Element {
  const quantityLabel = formatQuantity(row.quantity);
  const [quantityDraft, setQuantityDraft] = useState(quantityLabel);
  useEffect(() => {
    setQuantityDraft(quantityLabel);
  }, [quantityLabel, row.id]);

  const commitQuantity = (): void => {
    const parsed = Number.parseInt(quantityDraft.trim(), 10);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setQuantityDraft(quantityLabel);
      return;
    }
    if (parsed === toNumber(row.quantity)) {
      setQuantityDraft(quantityLabel);
      return;
    }
    onQuantityCommit(parsed);
  };

  const onQuantityKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") {
      event.currentTarget.blur();
    }
    if (event.key === "Escape") {
      setQuantityDraft(quantityLabel);
      event.currentTarget.blur();
    }
  };

  return (
    <tr className="border-b border-slate-100">
      <td className="px-4 py-2">
        <WofgWorkOrderLink buildOrderId={row.build_order_id} wofgNumber={row.finished_good_number} />
      </td>
      <td className="px-4 py-2">
        <MatchSkuLink
          sku={row.sku}
          skuHint={skuHint}
          skuHintsLoading={skuHintsLoading}
          onSkuHoverIntent={onSkuHoverIntent}
        />
      </td>
      <td className="px-4 py-2">
        <MatchSkuLink
          sku={row.tla_sku}
          skuHint={tlaSkuHint}
          skuHintsLoading={skuHintsLoading}
          onSkuHoverIntent={onSkuHoverIntent}
        />
      </td>
      <td className="px-4 py-2 text-right tabular-nums">
        {canEditQuantity ? (
          <Input
            type="number"
            min={1}
            step={1}
            aria-label="Match quantity"
            className="ml-auto h-8 w-24 text-right tabular-nums"
            value={quantityDraft}
            onChange={(event) => setQuantityDraft(event.target.value)}
            onBlur={commitQuantity}
            onKeyDown={onQuantityKeyDown}
          />
        ) : (
          quantityLabel
        )}
      </td>
      <td className="px-4 py-2 text-right tabular-nums">{formatMoney(row.service_cost)}</td>
      <td className="px-4 py-2 text-slate-800">{row.tla_order_numbers ?? EM_DASH}</td>
      {showActions ? (
        <td className="px-4 py-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            aria-label="Remove match"
            disabled={!canRemove}
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </Button>
        </td>
      ) : null}
    </tr>
  );
}

function FinishedGoodPickerRow({
  row,
  selected,
  onSelect,
}: {
  row: SuntronicInvoiceFinishedGoodRow;
  selected: boolean;
  onSelect: () => void;
}): JSX.Element {
  return (
    <tr className={cn("cursor-pointer", selected ? "bg-sky-50" : "hover:bg-slate-50")} onClick={onSelect}>
      <td className="px-4 py-2">{row.sku.trim() !== "" ? row.sku : EM_DASH}</td>
      <td className="px-4 py-2 text-right tabular-nums">{formatQuantity(row.quantity)}</td>
      <td className="px-4 py-2 text-right tabular-nums">{formatQuantity(row.quantity_matched ?? 0)}</td>
      <td className="px-4 py-2 text-slate-800">{row.mv_build_order_number ?? EM_DASH}</td>
      <td className="px-4 py-2">
        <WofgWorkOrderLink buildOrderId={row.build_order_id} wofgNumber={row.number} isolateClick />
      </td>
    </tr>
  );
}

function WorkOrderPickerRow({
  row,
  selected,
  onSelect,
}: {
  row: SuntronicInvoiceWorkOrderRow;
  selected: boolean;
  onSelect: () => void;
}): JSX.Element {
  return (
    <tr className={cn("cursor-pointer", selected ? "bg-sky-50" : "hover:bg-slate-50")} onClick={onSelect}>
      <td className="px-3 py-2 text-slate-800">{row.WorkOrderNo}</td>
      <td className="px-3 py-2 text-slate-800">{row.sb_number || EM_DASH}</td>
      <td className="px-3 py-2">
        {row.WorkOrderFinishedGoodSKU.trim() !== "" ? row.WorkOrderFinishedGoodSKU : EM_DASH}
      </td>
      <td className="px-3 py-2 text-slate-800">{row.WorkOrderStatus}</td>
    </tr>
  );
}
